import {Fragment, useEffect, useMemo} from "react";
import {useTranslation} from "react-i18next";
import {useDispatch} from "react-redux";
import {useSelector} from "react-redux";
import {
    Archive,
    BedDouble,
    Building2,
    Clock,
    Drill,
    Handshake,
    KeyRound,
    LucideIcon,
    Package,
    Sparkles,
    Ticket,
} from "lucide-react";
import SupBoardIcon from "./SupBoardIcon";
import SportsCourtIcon from "./SportsCourtIcon";
import {setTenantConfiguration} from "../../redux/slice/config/configSlice";
import {homeTiles, HomeTile, ROOM_TILE_LABEL_KEYS} from "./homeTiles";
import {RootState} from "../../redux/store";
import {TenantConfigService} from "../../types/tenantConfig";
import {getTenantName} from "../../util";
import {cn} from "../../util/cn";
import {SERVICE_ENTRY_ROUTE} from "./serviceEntryRoutes";
import TenantConfig from "../../util/tenantConfig";
import Header from "../layout/components/Header";
import TenantLogo from "../layout/components/TenantLogo";
import Banners from "./components/Banners";
import ServiceCard from "./components/ServiceCard";
import LocationGatedServiceCard from "./components/LocationGatedServiceCard";
import { isLocationListService } from "../bookLocation/common/components/LocationSelectionStep/UseCaseLocationSelectionStep";
import useServiceStorage from "../../hooks/useServiceStorage";
import {ServiceTypeSelectorOption} from "../../types/order";
import { i18n } from "i18n/i18n";
import { isRoomFamily } from "../../util/useCases";

// Icon per service type — replaces the former per-tenant background photos.
const serviceIcons: Record<TenantConfigService, LucideIcon> = {
    storage: Archive,
    rental: Drill,
    p2p: Handshake,
    parcel: Package,
    apartment: BedDouble,
    venue: Building2,
    hall: SportsCourtIcon as LucideIcon,
    keys: KeyRound,
    sup: SupBoardIcon as LucideIcon,
    voucher: Ticket,
    moreToCome: Sparkles,
};

const HomePage = () => {
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const {config} = useSelector((state: RootState) => state.tenantConfig);

    // Always render the "more to come" card last, regardless of tenant config order.
    const orderedServices = config?.services
        ? [...config.services].sort((a, b) => {
            if (a === "moreToCome") return 1;
            if (b === "moreToCome") return -1;
            return 0;
        })
        : [];

    // Only the tiles that stand for a category; a use case with none is not
    // represented here and falls through to the switch below.
    const categoryTiles = useMemo(
        () => homeTiles(
            orderedServices as string[],
            config?.categories ?? [],
            i18n.language,
            config?.standardCatalogue,
        ).filter((tile) => tile.label !== null || tile.standard),
        [orderedServices, config?.categories, config?.standardCatalogue, i18n.language]
    );

    const tenantName = getTenantName();

    // The label of a use case's standard-category tile (ADR-0026 §W): the
    // same words its single tile carries in the switch below, so a tenant who
    // adds a category sees its existing tile keep its name.
    // Every value without a room label of its own keeps the venue label, as before.
    const standardTileLabel = (serviceType: string): string =>
        t(ROOM_TILE_LABEL_KEYS[serviceType] ?? ROOM_TILE_LABEL_KEYS.venue);

    const {updateService} = useServiceStorage(
        "selectedService",
        ServiceTypeSelectorOption.APARTMENT
    );

    function handleUpdateServiceType(service: ServiceTypeSelectorOption) {
        updateService(service);
    }

    useEffect(() => {
        const retrieveTenantConfig = async () => {
            const config = await TenantConfig();
            dispatch(setTenantConfiguration(config));
        };
        retrieveTenantConfig();
    }, [dispatch]);

    // Render a graceful error state instead of throwing during render when the
    // tenant is misconfigured (the tenant comes from a build-time env var, so
    // this is a rare safety net rather than a normal path).
    if (!tenantName) {
        return (
            <div className="flex min-h-full items-center justify-center px-5 text-center text-foreground/70">
                {t("home.tenant_unavailable")}
            </div>
        );
    }

    return (
        <div className="min-h-full px-5 mb-20">
            <Header>
                <TenantLogo/>
            </Header>

            <Banners/>

            <span className="flex justify-center mb-4 text-base leading-5 tracking-[.02rem]">
        {t("home.do")}
      </span>
            {/* Services Section */}
            <div
                className={cn(
                    "items-center justify-center gap-4",
                    config?.services?.length > 2 ? "grid grid-cols-2" : "flex flex-col"
                )}
            >
                {orderedServices.map((serviceType) => {
                    // ADR-0026 §A: a use case the tenant has divided into
                    // product categories shows one tile PER CATEGORY instead of
                    // one for the use case — a municipality's meeting rooms and
                    // its sports pitches are not the same errand.
                    //
                    // Surgical on purpose. A use case with no categories falls
                    // straight through to the switch below, unchanged: same
                    // label, same route, same badge and icon logic it has
                    // today. That is every tenant right now, and every use case
                    // a tenant never divides.
                    const ownTiles: HomeTile[] = categoryTiles.filter(
                        (tile) => tile.useCase === serviceType
                    );
                    if (ownTiles.length > 0) {
                        // Wrapped in a Fragment so this branch returns ONE
                        // element like every switch branch below, rather than
                        // an array. React would flatten the array happily, but
                        // the mixed return type is a Sonar reliability finding
                        // (sonarjs/function-return-type) and the untyped
                        // pre-check cannot see it.
                        //
                        // A Fragment renders no DOM node, so each card stays a
                        // direct child of the `grid`/`flex` container above and
                        // the layout is byte-identical to returning the array.
                        return (
                            <Fragment key={serviceType}>
                                {ownTiles.map((tile) => {
                                    const cardProps = {
                                        to: tile.to,
                                        icon: serviceIcons[serviceType],
                                        // The standard category's tile is named
                                        // like the use case's own tile was.
                                        label: tile.label ?? standardTileLabel(serviceType),
                                        service: serviceType as ServiceTypeSelectorOption,
                                        onClickService: handleUpdateServiceType,
                                        singleLine: true,
                                    };
                                    return isLocationListService(serviceType) ? (
                                        <LocationGatedServiceCard
                                            key={tile.id}
                                            locationService={serviceType}
                                            {...cardProps}
                                        />
                                    ) : (
                                        <ServiceCard key={tile.id} {...cardProps} />
                                    );
                                })}
                            </Fragment>
                        );
                    }

                    // A room use case's single tile (venue, apartment, hall).
                    if (isRoomFamily(serviceType)) {
                        return (
                            <ServiceCard
                                key={serviceType}
                                service={serviceType as ServiceTypeSelectorOption}
                                onClickService={handleUpdateServiceType}
                                label={t(ROOM_TILE_LABEL_KEYS[serviceType])}
                                to={SERVICE_ENTRY_ROUTE[serviceType]}
                                icon={serviceIcons[serviceType]}
                            />
                        );
                    }
                    switch (serviceType) {
                        case "storage":
                            return (
                                <ServiceCard
                                    key="storage"
                                    service={ServiceTypeSelectorOption.STORAGE}
                                    onClickService={handleUpdateServiceType}
                                    label={tenantName.toLowerCase() === "segmenta"
                                        ? t("home.temporary_storage_segmenta")
                                        : t("home.temporary_storage")}
                                    to={SERVICE_ENTRY_ROUTE.storage}
                                    icon={serviceIcons.storage}
                                    overlayIcon={Clock}
                                />
                            );
                        case "rental":
                            return (
                                <LocationGatedServiceCard
                                    key="rental"
                                    locationService="rental"
                                    service={ServiceTypeSelectorOption.RENTAL}
                                    onClickService={handleUpdateServiceType}
                                    label={t("home.product_rental")}
                                    to={SERVICE_ENTRY_ROUTE.rental}
                                    icon={serviceIcons.rental}
                                    overlayIcon={Clock}
                                />
                            );
                        case "p2p":
                            return (
                                <LocationGatedServiceCard
                                    key="p2p"
                                    locationService="p2p"
                                    service={ServiceTypeSelectorOption.P2P}
                                    onClickService={handleUpdateServiceType}
                                    label={t("home.private_handovers")}
                                    to={SERVICE_ENTRY_ROUTE.p2p}
                                    icon={serviceIcons.p2p}
                                />
                            );
                        case "parcel":
                            return (
                                <LocationGatedServiceCard
                                    key="parcel"
                                    locationService="parcel"
                                    service={ServiceTypeSelectorOption.PARCEL}
                                    onClickService={handleUpdateServiceType}
                                    label={t("home.click_and_collect")}
                                    badge="Beta"
                                    to={SERVICE_ENTRY_ROUTE.parcel}
                                    icon={serviceIcons.parcel}
                                />
                            );
                        case "keys":
                            return (
                                <ServiceCard
                                    key="keys"
                                    service={ServiceTypeSelectorOption.KEYS}
                                    onClickService={handleUpdateServiceType}
                                    label={t("home.key_handover")}
                                    to={SERVICE_ENTRY_ROUTE.keys}
                                    icon={serviceIcons.keys}
                                />
                            );
                        case "sup":
                            return (
                                <LocationGatedServiceCard
                                    key="sup"
                                    locationService="sup"
                                    service={ServiceTypeSelectorOption.SUP}
                                    onClickService={handleUpdateServiceType}
                                    label={t("home.product_sup")}
                                    to={SERVICE_ENTRY_ROUTE.sup}
                                    icon={serviceIcons.sup}
                                />
                            );
                        case "voucher":
                            return (
                                <ServiceCard
                                    key="voucher"
                                    service={ServiceTypeSelectorOption.VOUCHER}
                                    onClickService={handleUpdateServiceType}
                                    label={t("home.voucher_card")}
                                    to={SERVICE_ENTRY_ROUTE.voucher}
                                    icon={serviceIcons.voucher}
                                />
                            );
                        case "moreToCome":
                            return (
                                <ServiceCard
                                    key="moreToCome"
                                    service={ServiceTypeSelectorOption.MORE_TO_COME}
                                    onClickService={handleUpdateServiceType}
                                    label={config?.content?.[i18n.language]?.moreToCome?.alternative_title || t("home.more_to_come")}
                                    to={SERVICE_ENTRY_ROUTE.moreToCome}
                                    icon={serviceIcons.moreToCome}
                                />
                            );
                        default:
                            return null;
                    }
                })}
            </div>
        </div>
    );
};
export default HomePage;
